/** How long a warning waits for the click that confirms it. */
const ARMED_MS = 10_000;

/**
 * A click sooner than this after the warning showed, or after the click before it, is not a decision:
 * a confirmation the user never read is no confirmation.
 */
export const MIN_CONFIRM_MS = 400;

export type ConfirmationClick = 'armed' | 'confirmed' | 'ignored';

/** When a click was made, and when the one before it was. */
export interface ConfirmationPress {
  at: number;
  previous: number | null;
}

/**
 * A two-click confirmation for a send that cannot be undone. Office.js has no dialog here and
 * window.confirm is blocked in some hosts, so the first click says what would happen and the second
 * does it. A click confirms only what the first was for: the same `key`, the sheet it would send.
 *
 * A click is timed when it is made, in its listener, while the checks that decide what it does may run
 * for a while after. Timed at the end of those checks, a click made the moment a slow first click
 * showed its warning would look like one made long after it.
 */
export class TwoClickConfirmation {
  private armed: { key: string; shownAt: number } | null = null;
  private lastPressAt: number | null = null;

  constructor(private readonly now: () => number = () => Date.now()) {}

  /** A click, timed now. Taken first in the listener, also for a click that is then refused as busy. */
  pressed(): ConfirmationPress {
    const at = this.now();
    const press = { at, previous: this.lastPressAt };
    this.lastPressAt = at;
    return press;
  }

  /**
   * What `press` does, once its checks are done. A first click, one for another key or one after the
   * wait ran out arms, and the warning shows from now. A click confirms only when it was made at least
   * MIN_CONFIRM_MS after the warning showed and after the click before it; any other is ignored, so
   * neither a burst nor a click made while the warning was still being worked out confirms.
   */
  click(key: string, press: ConfirmationPress): ConfirmationClick {
    const armed = this.armed;
    if (armed === null || armed.key !== key || press.at - armed.shownAt >= ARMED_MS) {
      this.armed = { key, shownAt: this.now() };
      return 'armed';
    }
    const unread = press.at - armed.shownAt < MIN_CONFIRM_MS;
    const burst = press.previous !== null && press.at - press.previous < MIN_CONFIRM_MS;
    if (unread || burst) return 'ignored';
    this.armed = null;
    return 'confirmed';
  }

  /**
   * Unarmed again. The pane calls it when it is reset or read again, when the template or the sheet it
   * acts on changes, and when another journal action starts: the warning was for what it showed then.
   */
  reset(): void {
    this.armed = null;
  }
}
